import {
  FavoritesFragment,
  UpdateFavoriteListMutation,
  UpdateFavoriteListMutationVariables,
  DeleteFavoriteListMutation,
  DeleteFavoriteListMutationVariables,
  FavoriteListItemFragment,
  UpdateFavoriteListNameMutation,
  UpdateFavoriteListNameMutationVariables,
  GetExtendedFavoritesNoRecommendationsQuery,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { GET_EXTENDED_FAVORITES_NO_RECOMMENDATIONS } from '@dustin-web/microsite-data/src/graphql/product/get-customer-product-lists.graphql'
import {
  createProductList,
  createProductListItem,
} from '@dustin-web/microsite-data/src/utils/create-product-list'
import { UPDATE_FAVORITE_LIST } from '@dustin-web/microsite-data/src/graphql/product/update-favorite-list.graphql'
import { DELETE_FAVORITE_LIST } from '@dustin-web/microsite-data/src/graphql/product/delete-favorite-list.graphql'
import { UPDATE_FAVORITE_LIST_NAME } from '@dustin-web/microsite-data/src/graphql/product/update-favorite-list-name'
import { useGraphql, useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import { useFavorites } from '@dustin-web/microsite-data/src/hooks/use-favorites-internal'
export type UpdateFavorites = ReturnType<typeof useExtendedFavorites>['updateFavorites']

export type FavoriteProductList = FavoritesFragment['productLists']
export type UpdateFavoriteListName = ReturnType<
  typeof useExtendedFavorites
>['updateFavoriteListName']

export const useExtendedFavorites = () => {
  const {
    data: favoritesData,
    isValidating: loading,
    error,
    mutate,
  } = useGraphql<GetExtendedFavoritesNoRecommendationsQuery>(
    GET_EXTENDED_FAVORITES_NO_RECOMMENDATIONS,
    {
      swrOptions: {
        revalidateOnMount: false,
      },
    }
  )

  const [updateFavoritesMutation] = useGraphqlMutation<
    UpdateFavoriteListMutation,
    UpdateFavoriteListMutationVariables
  >(UPDATE_FAVORITE_LIST, {})

  const [deleteFavoriteListMutation] = useGraphqlMutation<
    DeleteFavoriteListMutation,
    DeleteFavoriteListMutationVariables
  >(DELETE_FAVORITE_LIST, {})

  const [updateFavoritesNameMutation] = useGraphqlMutation<
    UpdateFavoriteListNameMutation,
    UpdateFavoriteListNameMutationVariables
  >(UPDATE_FAVORITE_LIST_NAME, {})

  const { refetchFavorites } = useFavorites()

  const updateFavoriteListName = (favoriteListName: string, updatedFavoriteListName: string) => {
    const oldProductList = favoritesData?.favorites?.productLists ?? []

    const updatedFavoriteList = oldProductList.find(x => x?.productListName === favoriteListName)

    const updatedData = {
      favorites: {
        __typename: 'Favorites',
        productLists: [
          ...oldProductList.filter(x => x?.productListName !== favoriteListName),
          ...[{ ...updatedFavoriteList, productListName: updatedFavoriteListName }],
        ],
      },
    } as GetExtendedFavoritesNoRecommendationsQuery

    mutate(
      async () => {
        await updateFavoritesNameMutation({
          variables: {
            favoriteListName,
            updatedFavoriteListName,
          },
        })
        refetchFavorites()

        return updatedData
      },
      {
        revalidate: false,
        optimisticData: updatedData,
      }
    )
  }

  const updateFavorites = (
    productId: string,
    displayName: string,
    favoriteListName: string,
    quantity: number,
    favoriteListId: number,
    cartItemId?: number | null,
    comment?: string | null
  ) => {
    mutate(
      async () => {
        const newData = await updateFavoritesMutation({
          variables: {
            favoriteListId,
            favoriteListName,
            productErpIdentifier: productId,
            quantity,
            cartItemId,
            comment,
          },
        })

        const oldProductList = favoritesData?.favorites?.productLists ?? []

        const productListId = newData?.updateFavoriteListWithProduct?.productListId
        const productListItemId = newData?.updateFavoriteListWithProduct?.productListItemId

        let updatedProductList

        const existingFavoriteList = oldProductList.find(listItem => listItem?.id === productListId)
        const existingItem = existingFavoriteList?.items.find(item => item.id === productListItemId)

        if (existingFavoriteList) {
          if (quantity > 0) {
            // Update existing item
            if (existingItem) {
              const updatedItem = { ...existingItem, quantity: quantity }

              const updatedFavoriteListItems = [
                ...existingFavoriteList.items.filter(item => item.id !== productListItemId),
                updatedItem,
              ].sort((a, b) => a.id - b.id)

              const updatedFavoriteList: FavoriteListItemFragment = {
                ...existingFavoriteList,
                items: updatedFavoriteListItems,
              }

              updatedProductList = [
                ...oldProductList.filter(i => i?.id !== productListId),
                updatedFavoriteList,
              ]

              // Add item to list
            } else {
              const updatedFavoriteListItems = [
                ...existingFavoriteList.items,
                createProductListItem(productListItemId, productId, displayName),
              ]

              const updatedFavoriteList = {
                ...existingFavoriteList,
                items: updatedFavoriteListItems,
              }

              updatedProductList = [
                ...oldProductList.filter(i => i?.id !== productListId),
                {
                  updatedFavoriteList,
                },
              ]
            }
            // Delete item from list
          } else {
            const updatedFavoriteListItems = existingFavoriteList.items.filter(
              item => item.productErpIdentifier !== productId
            )
            const updatedFavoriteList: FavoriteListItemFragment = {
              ...existingFavoriteList,
              items: updatedFavoriteListItems,
            }
            updatedProductList = [
              ...oldProductList.filter(i => i?.id !== productListId),
              updatedFavoriteList,
            ]
          }
        } else {
          // Create new list and add item
          updatedProductList = [
            ...oldProductList,
            createProductList(
              productListId,
              favoriteListName,
              productListItemId,
              productId,
              displayName
            ),
          ]
        }

        const updatedData = {
          favorites: { __typename: 'Favorites', productLists: updatedProductList },
        } as GetExtendedFavoritesNoRecommendationsQuery

        refetchFavorites()
        return updatedData
      },
      {
        revalidate: false,
      }
    )
  }

  const deleteFavoriteList = (id: number) => {
    mutate(
      async () => {
        const newData = await deleteFavoriteListMutation({ variables: { id } })
        refetchFavorites()

        const cachedFavoritesLists = favoritesData?.favorites?.productLists ?? []
        const updatedData = {
          favorites: {
            __typename: 'Favorites',
            productLists: cachedFavoritesLists?.filter(
              list => list?.id !== newData?.deleteFavoriteList?.id
            ),
          },
        }

        return updatedData
      },
      {
        revalidate: false,
      }
    )
  }

  if (error) {
    throw error
  }
  return {
    loading,
    error,
    favoritesData: favoritesData?.favorites?.productLists ?? [],
    refetchExtendedFavorites: mutate,
    updateFavorites,
    deleteFavoriteList,
    updateFavoriteListName,
  }
}
